import { useMemo, useState } from 'react';
import { Search, Layers } from 'lucide-react';
import { commands } from '@/data/commands';
import { toneForCategory } from '@/data/categoryTone';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { FilterChips } from '@/components/ui/FilterChips';
import { EmptyState } from '@/components/ui/EmptyState';
import { Alert } from '@/components/ui/Alert';

const categories = Array.from(new Set(commands.map((c) => c.category))).sort();

export function CommandsPage() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string | null>(null);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return commands.filter((c) => {
      if (category && c.category !== category) return false;
      if (!q) return true;
      return (
        c.name.toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q)
      );
    });
  }, [query, category]);

  return (
    <div className="px-4 md:px-8 py-12 max-w-4xl mx-auto">
      <h1 className="text-h1 text-text-primary mb-2">Commands</h1>
      <p className="text-body text-text-secondary max-w-2xl mb-4">
        {commands.length} top-level commands and command groups, extracted directly from the bot's
        source. Some entries are groups with many subcommands underneath — the count is shown
        where that applies.
      </p>

      <Alert
        tone="info"
        title="This is a static snapshot, not a live feed"
        description="Once the Bot Bridge exists, this page will read live from the bot's own command introspection (the same system that powers /help) instead of a bundled dataset — see docs/IMPLEMENTATION_PLAN.md Feature 25."
      />

      <div className="mt-6 mb-4">
        <Input
          type="search"
          placeholder="Search commands..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search commands"
        />
      </div>

      <div className="mb-6">
        <FilterChips options={categories} selected={category} onSelect={setCategory} />
      </div>

      {visible.length === 0 ? (
        <EmptyState icon={Search} title="No commands match" description="Try a different search term or category." />
      ) : (
        <div className="bg-bg-surface border border-border-subtle rounded-md divide-y divide-border-subtle">
          {visible.map((cmd) => (
            <div key={`${cmd.category}-${cmd.name}`} className="flex items-start gap-3 p-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-mono text-text-primary">/{cmd.name}</code>
                  <Badge tone={toneForCategory(cmd.category)}>{cmd.category}</Badge>
                  {cmd.type === 'context_menu' ? <Badge tone="neutral">Right-click</Badge> : null}
                  {cmd.subcommandCount ? (
                    <span className="inline-flex items-center gap-1 text-micro text-text-tertiary">
                      <Layers size={12} aria-hidden="true" />
                      {cmd.subcommandCount} subcommands
                    </span>
                  ) : null}
                </div>
                {cmd.description ? (
                  <p className="text-small text-text-secondary mt-1">{cmd.description}</p>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
